// src/components/admin/OrganizationHierarchyBuilder.jsx
// Organization Hierarchy Builder - FLAT ICONS ONLY

import React, { useState, useEffect } from 'react';
import useTabFromUrl from '../../hooks/useTabFromUrl';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { fetchOrgHierarchy } from '../../utils/api';

// Flat Icon Component
function FlatIcon({ type, size = 20 }) {
  const icons = {
    market: { text: 'MKT', color: '#00CED1' },
    region: { text: 'RGN', color: '#2C5FA8' },
    building: { text: 'BLD', color: '#10B981' },
    service: { text: 'SVC', color: '#F59E0B' }
  };

  const icon = icons[type] || icons.market;

  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '4px',
      background: icon.color,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size * 0.35,
      fontWeight: '600',
      letterSpacing: '-0.3px',
      flexShrink: 0
    }}>
      {icon.text}
    </div>
  );
}

const FALLBACK_MARKETS = [
  {
    id: 1,
    name: 'Mid-Atlantic Market',
    code: 'MA',
    regions: [
      {
        id: 1,
        name: 'Columbia Medical Center',
        buildings: 2,
        serviceLines: ['Primary Care', 'Pediatrics', 'Orthopedics']
      },
      {
        id: 2,
        name: 'Baltimore Region',
        buildings: 0,
        serviceLines: []
      }
    ]
  }
];

function OrganizationHierarchyBuilder() {
  const { getToken } = useAuth();
  const [activeTab, setActiveTab] = useTabFromUrl('markets');
  const [markets, setMarkets] = useState(FALLBACK_MARKETS);

  useEffect(() => {
    fetchOrgHierarchy(getToken)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setMarkets(data);
        }
      })
      .catch(() => {
        // Keep fallback data on error
      });
  }, [getToken]);

  const tabs = [
    { id: 'markets', label: 'Markets', iconType: 'market' },
    { id: 'regions', label: 'Regions/Hubs', iconType: 'region' },
    { id: 'buildings', label: 'Buildings', iconType: 'building' },
    { id: 'services', label: 'Service Lines', iconType: 'service' }
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Organization Hierarchy Builder</h1>
          <p className="dashboard-subtitle">
            Configure your organizational structure: Markets → Regions → Buildings & Service Lines
          </p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-nn-border mb-4 overflow-x-auto">
        {tabs.map(tab => (
          <div
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FlatIcon type={tab.iconType} size={16} />
              <span>{tab.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '24px' }}>
        {activeTab === 'markets' && <MarketsTab markets={markets} />}
        {activeTab === 'regions' && <RegionsTab markets={markets} />}
        {activeTab === 'buildings' && <BuildingsTab />}
        {activeTab === 'services' && <ServicesTab />}
      </div>
    </div>
  );
}

function MarketsTab({ markets }) {
  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Markets</h2>
        <Button variant="primary" size="sm">+ Add Market</Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {markets.map(market => (
          <div key={market.id} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
            <div className="p-5">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FlatIcon type="market" size={32} />
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: '500', margin: 0 }}>{market.name}</h3>
                    <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0 0' }}>Code: {market.code}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button variant="default" size="sm">Edit</Button>
                  <Button variant="danger" size="sm">Delete</Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="section-header" style={{ marginTop: '40px' }}>
        <h3 className="section-title">Organization Structure Overview</h3>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="p-5">
          {markets.map(market => (
            <div key={market.id} style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <FlatIcon type="market" size={24} />
                <span style={{ fontSize: '16px', fontWeight: '500' }}>{market.name}</span>
              </div>
              <div style={{ marginLeft: '36px', borderLeft: '2px solid #E5E7EB', paddingLeft: '16px' }}>
                {market.regions.map(region => (
                  <div key={region.id} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <FlatIcon type="region" size={20} />
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>{region.name}</span>
                    </div>
                    <div style={{ marginLeft: '28px', fontSize: '13px', color: '#6B7280' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <FlatIcon type="building" size={16} />
                        <span>Buildings: {region.buildings}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FlatIcon type="service" size={16} />
                        <span>Service Lines: {region.serviceLines.length > 0 ? region.serviceLines.join(', ') : 'None'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RegionsTab({ markets }) {
  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Regions / Hubs</h2>
        <Button variant="primary" size="sm">+ Add Region/Hub</Button>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div style={{ padding: 0 }}>
          <table className="nn-table">
            <thead>
              <tr>
                <th>Region/Hub Name</th>
                <th>Market</th>
                <th style={{ textAlign: 'center' }}>Buildings</th>
                <th style={{ textAlign: 'center' }}>Service Lines</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {markets.flatMap(market =>
                market.regions.map(region => (
                  <tr key={`${market.id}-${region.id}`}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FlatIcon type="region" size={20} />
                        <span style={{ fontWeight: '500' }}>{region.name}</span>
                      </div>
                    </td>
                    <td className="text-text-muted">{market.name}</td>
                    <td className="text-text-muted" style={{ textAlign: 'center' }}>{region.buildings}</td>
                    <td className="text-text-muted" style={{ textAlign: 'center' }}>{region.serviceLines.length}</td>
                    <td style={{ textAlign: 'center' }}>
                      <Button variant="default" size="sm" style={{ flex: 'none', marginRight: '6px' }}>Edit</Button>
                      <Button variant="danger" size="sm" style={{ flex: 'none' }}>Delete</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BuildingsTab() {
  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Buildings</h2>
        <Button variant="primary" size="sm">+ Add Building</Button>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="p-5">
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6B7280',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            <FlatIcon type="building" size={48} />
            <p style={{ fontSize: '14px', margin: 0 }}>
              No buildings configured yet. Click "+ Add Building" to get started.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ServicesTab() {
  const serviceLines = [
    { id: 1, name: 'Primary Care', regions: 5, color: '#00CED1' },
    { id: 2, name: 'Pediatrics', regions: 3, color: '#2C5FA8' },
    { id: 3, name: 'Orthopedics', regions: 2, color: '#10B981' }
  ];

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Service Lines</h2>
        <Button variant="primary" size="sm">+ Add Service Line</Button>
      </div>

      <div className="grid grid-cols-3 gap-5">
        {serviceLines.map(service => (
          <div key={service.id} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua" style={{ borderLeftColor: service.color }}>
            <div className="p-5">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <FlatIcon type="service" size={24} />
                <h3 style={{ fontSize: '16px', fontWeight: '500', margin: 0 }}>{service.name}</h3>
              </div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
                Active in {service.regions} regions
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
                <Button variant="default" size="sm">Edit</Button>
                <Button variant="danger" size="sm">Delete</Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default OrganizationHierarchyBuilder;
